/**
 * Scheduling Debug — admin-only trace panel.
 *
 * Consumes `GET /api/scheduling/debug?date=YYYY-MM-DD` which runs every
 * appointment on that date through the SAME validator that the public
 * booking + internal calendar use.  If a row here says FAIL, it will
 * fail on booking too — no exceptions.  If the calendar UI shows a green
 * appointment card while this panel says FAIL, the calendar UI has a
 * caching bug — not the engine.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, XCircle, MinusCircle, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { fmtTime24 } from "@/lib/time";
import { DateDMYInput } from "@/components/DateDMYInput";

const STATUS_ICON = {
  PASS: <CheckCircle2 className="w-4 h-4 text-pine" />,
  FAIL: <XCircle      className="w-4 h-4 text-[#B94A48]" />,
  SKIP: <MinusCircle  className="w-4 h-4 text-ink-soft" />,
  INFO: <MinusCircle  className="w-4 h-4 text-ink-mid" />,
};

export default function SchedulingDebug() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState({});

  const isAdmin = user?.role === "admin";

  const load = async (d) => {
    setLoading(true);
    try {
      const { data } = await api.get("/scheduling/debug", { params: { date: d } });
      setData(data);
    } catch (e) {
      toast.error(apiError(e, "Could not load scheduling debug"));
    } finally { setLoading(false); }
  };
  useEffect(() => { if (isAdmin) load(date); /* eslint-disable-next-line */ }, [isAdmin, date]);

  if (!isAdmin) {
    return (
      <div className="max-w-lg mx-auto py-16 text-center" data-testid="sd-forbidden">
        <div className="overline text-ink-soft">Admin only</div>
        <h1 className="mt-2 font-display text-2xl">Scheduling Debug is restricted</h1>
      </div>
    );
  }

  const valid   = (data?.appointments || []).filter(a => a.overall === "VALID").length;
  const invalid = (data?.appointments || []).filter(a => a.overall === "INVALID").length;

  return (
    <div className="space-y-5" data-testid="scheduling-debug">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="overline text-ink-soft">Admin · scheduling diagnostics</div>
          <h1 className="mt-2 font-display text-4xl tracking-tight">Scheduling Debug</h1>
          <p className="mt-2 text-sm text-ink-mid max-w-2xl">
            Every appointment run through the exact same validator used by the public booking + internal calendar.
            If a row shows FAIL, it will fail on new booking too. If the calendar shows a green card here for a FAIL row,
            the calendar UI has a caching bug — not the engine.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateDMYInput value={date} onChange={(e) => setDate(e.target.value)}
                 data-testid="sd-date"
                 className="rounded-full border border-[#E8EAE9] bg-white px-3 py-2 text-sm focus:outline-none focus:border-pine" />
          <button onClick={() => load(date)} disabled={loading} data-testid="sd-refresh"
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#E8EAE9] hover:border-pine text-sm px-4 py-2 disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Summary label="Date"            value={data.date} />
          <Summary label="Timezone"        value={data.timezone} />
          <Summary label="Valid"           value={valid}   color="pine" />
          <Summary label="Invalid"         value={invalid} color={invalid > 0 ? "red" : "ink-soft"} />
        </div>
      )}

      {(data?.appointments || []).length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E8EAE9] bg-linen/40 p-6 text-center text-sm text-ink-mid">
          No appointments on this date.
        </div>
      ) : (
        <div className="space-y-2">
          {data.appointments.map((a) => (
            <div key={a.id} className="rounded-2xl border border-[#E8EAE9] bg-white overflow-hidden" data-testid={`sd-row-${a.id}`}>
              <button className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-linen/30"
                      onClick={() => setOpen((o) => ({ ...o, [a.id]: !o[a.id] }))}>
                {open[a.id] ? <ChevronDown className="w-4 h-4 text-ink-soft" />
                            : <ChevronRight className="w-4 h-4 text-ink-soft" />}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                    <span className="font-medium">{a.patient}</span>
                    <span className="text-xs text-ink-mid">Dr. {a.doctor}</span>
                    <span className="text-xs text-ink-soft font-mono">{a.chair}</span>
                    {a.historical && <span className="text-[10px] uppercase tracking-widest text-ink-soft border border-[#E8EAE9] rounded-full px-1.5">Historical</span>}
                  </div>
                  <div className="text-[11px] text-ink-mid mt-0.5">
                    {fmtTime24(a.start)}–{fmtTime24(new Date(new Date(a.start).getTime() + a.duration_min * 60000))} · {a.procedure || "—"}
                  </div>
                </div>
                <div className={`shrink-0 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full ${
                  a.overall === "VALID" ? "bg-pine/10 text-pine" : "bg-[#FCECEC] text-[#B94A48]"
                }`} data-testid={`sd-overall-${a.id}`}>
                  {a.overall}
                </div>
              </button>
              {open[a.id] && (
                <div className="border-t border-[#F0EBE0] bg-linen/20 px-4 py-3" data-testid={`sd-trace-${a.id}`}>
                  <table className="w-full text-xs">
                    <tbody>
                      {a.trace.map((t, i) => (
                        <tr key={i} className="border-b border-[#F0EBE0] last:border-b-0">
                          <td className="py-1.5 pr-3 w-8">{STATUS_ICON[t.status] || STATUS_ICON.INFO}</td>
                          <td className="py-1.5 pr-3 w-52 font-medium">{t.label}</td>
                          <td className="py-1.5 text-ink-mid font-mono text-[11px]">{t.detail || "—"}</td>
                        </tr>
                      ))}
                      {a.overall === "INVALID" && a.reason && (
                        <tr>
                          <td colSpan={3} className="pt-2 text-[11px] text-[#B94A48]">
                            <strong>Reason:</strong> {a.reason} · <em>{a.message}</em>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {data?.source_note && (
        <div className="rounded-2xl border border-dashed border-[#E8EAE9] bg-linen/40 p-4 text-[11px] text-ink-mid">
          {data.source_note}
        </div>
      )}
    </div>
  );
}

function Summary({ label, value, color = "ink" }) {
  const cls = { pine: "text-pine", red: "text-[#B94A48]", "ink-soft": "text-ink-soft", ink: "" }[color] || "";
  return (
    <div className="rounded-2xl border border-[#E8EAE9] bg-white p-4">
      <div className="text-[10px] uppercase tracking-widest text-ink-soft">{label}</div>
      <div className={`mt-1 font-display text-3xl ${cls}`}>{value}</div>
    </div>
  );
}
